import React, { useCallback, useEffect, useState } from 'react'
import { Hook } from '../hooks/Hook'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { CallBotLayoutInfo } from './callBot/types'
import { useSingletonMVUReducer } from '~/mvuReducer'
import { UploadAttrs, WebRecorder2Action, WebRecorder2SideEffect, WebRecorder2State } from '~/webRecorder2/webRecorder2'
import { useUnsubmittedRecordings } from '~/webRecorder2/useUnsubmittedRecordings'
import { CallBotSidebarView } from './callBot/react'
import { UnsubmittedRecordingView } from './UnsubmittedRecordingView'
import { trackAnalytics, uploadRecording } from '~/webRecorder2/liveView'
import { classifySubjectId, matchesSubjectId } from '~/utils/string'
import { useWebRecorderPubSubSubscribe } from '~/webRecorder2/useWebRecorderPubSubSubscribe'
import * as GlobalPubSub from '~/pubSub'
import { Toast } from '~/toasts/toasts'
import { usePhxEventListener } from '~/utils/useWindowEventListener'
import * as CallBot from './callBot/callBot'
import { isEqual } from '~/utils/array'

export function LiveNotetaker({ hook }: { hook: Hook }) {
  const canJoinMyCall = hook.el.hasAttribute('data-can-join-my-call')
  const canUseWebRecorder = hook.el.hasAttribute('data-can-use-web-recorder')

  // Need to test this without configuring a bunch of meetings in your calendar? Try hardcoding
  // the layout like this:
  //
  // const callBotLayoutInfos: CallBotLayoutInfo[] = [
  //   {
  //     id: '1',
  //     status: 'Hello world',
  //     status_indicator: 'idle',
  //     buttons: ['send_notetaker'],
  //     service_logo: { path: '/images/logo-google.svg', className: 'h-2.5' },
  //     timestampUnixMs: 1717756800000,
  //   },
  //   {
  //     id: '2',
  //     status: 'This is a status',
  //     status_indicator: 'recording',
  //     buttons: ['pause_notetaker', 'stop_notetaker', 'resume_notetaker'],
  //     service_logo: { path: '/images/logo-zoom.svg', className: 'h-2.5' },
  //     question_mark_tooltip: 'This is a tooltip',
  //     timestampUnixMs: Date.now(),
  //   },
  //   {
  //     id: '4',
  //     status: 'This is a status',
  //     status_indicator: 'recording',
  //     buttons: ['join_my_meeting'],
  //     service_logo: { path: '/images/logo-microsoft.svg', className: 'h-2.5' },
  //   },
  //   {
  //     id: '5',
  //     status: 'This is a status',
  //     status_indicator: 'recording',
  //     buttons: ['send_ad_hoc_notetaker', 'in_twilio_call_recording', 'completed_twilio_call_recording'],
  //     service_logo: { path: '/images/logo-redtail.svg', className: 'h-5' },
  //     join_url: 'https://www.example.com',
  //   },
  // ]
  const layoutInfos: CallBotLayoutInfo[] = JSON.parse(hook.el.dataset.callBotLayoutInfos || '[]')

  const [callBotState, callBotDispatch] = useSingletonMVUReducer<CallBot.State, CallBot.Action, CallBot.SideEffect>(
    'callBot',
    {
      initializer: CallBot.initialize(layoutInfos),
      reducer: CallBot.applyAction,
      sideEffectExecutor: CallBot.makeSideEffectExecutor({
        buttonClicked: (buttonType, id, url, eventIntegrationReferenceId) => {
          hook.pushEvent(buttonType, {
            'info-id': id,
            url,
            event_integration_reference_id: eventIntegrationReferenceId,
          })
        },
      }),
    }
  )

  // Update call bot state when server sends new layout info
  const layoutInfosChanged = !isEqual(layoutInfos, callBotState?.layoutInfos)

  useEffect(() => {
    if (layoutInfosChanged) {
      callBotDispatch?.({ action: 'update-layout-infos', layoutInfos })
    }
  }, [layoutInfosChanged, callBotDispatch])

  const canDownload = hook.el.hasAttribute('data-can-download')
  const showDownloadButtonBeforeRecording = hook.el.hasAttribute('data-show-download-button-before-recording')

  const [recordingState, recordingDispatch] = useSingletonMVUReducer<
    WebRecorder2State,
    WebRecorder2Action,
    WebRecorder2SideEffect
  >('webRecorder2')

  const upload = useCallback(
    async (attrs: UploadAttrs) => {
      try {
        await uploadRecording(hook, attrs)
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error('LiveNotetaker upload failed', error)

        GlobalPubSub.publish('show-toast', {
          type: 'error',
          content: 'Uploading failed. Please try again or contact support.',
          durationMs: 10_000,
        } as Toast)
      }
    },
    [hook]
  )

  const { localRecordings, removeRecording, removeZeroLengthRecordings } = useUnsubmittedRecordings(hook, {
    canUseWebRecorder,
  })

  usePhxEventListener('cloud_file_uploading', (event) => {
    const { subject_id } = event.detail

    if (subject_id) {
      // TODO: Instead of removing, mark this as uploading/processing
      removeRecording(classifySubjectId(subject_id))
    }
  })

  // On page change, we need to clean out any placeholder local recordings we created
  // for a previous meeting detail/prepare page that we didn't actually start recording on.
  useEffect(() => {
    const handler = () => {
      const meetingState = document.getElementById('react-meeting-state')
      const meetingDetailSubjectId = meetingState?.dataset.subjectId

      const activeRecordingSubjectId =
        recordingState &&
        !['initial', 'initialized'].includes(recordingState.mode) &&
        'storage' in recordingState &&
        recordingState.storage.subjectId

      const exemptSubjectIds = [meetingDetailSubjectId, activeRecordingSubjectId].filter((s) => typeof s === 'string')
      removeZeroLengthRecordings(exemptSubjectIds)
    }

    GlobalPubSub.subscribe('navigated', handler)
    return () => GlobalPubSub.unsubscribe('navigated', handler)
  }, [recordingState, removeZeroLengthRecordings])

  const callBotLayoutInfos = callBotState?.layoutInfos || []
  const renderableElementCount = callBotLayoutInfos.length + localRecordings.length

  const defaultSubjectId = window.location.pathname.match(/^\/(meetings|prepare)\/([^/?]+)/)?.at(2)
  const defaultSubjectIndex = localRecordings.findIndex((recording) => matchesSubjectId(recording, defaultSubjectId))
  const defaultIndex: number = defaultSubjectIndex >= 0 ? callBotLayoutInfos.length + defaultSubjectIndex : 0
  const [currentIndexRaw, setCurrentIndex] = useState(defaultIndex)

  // Cope with recordings being removed out from under us
  const currentIndex = Math.max(0, Math.min(currentIndexRaw, renderableElementCount - 1))

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < renderableElementCount - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  const focusRecording = useCallback(
    ({ subjectId }: { subjectId: string }) => {
      const unsubmittedRecordingIdx = localRecordings.findIndex((recording) => matchesSubjectId(recording, subjectId))

      if (unsubmittedRecordingIdx >= 0) {
        setCurrentIndex(callBotLayoutInfos.length + unsubmittedRecordingIdx)
      }
    },
    [localRecordings, setCurrentIndex]
  )

  // If the user starts a recording from elsewhere in the UI, we should
  // focus that recording here in the sidebar.
  useWebRecorderPubSubSubscribe<{ subjectId: string }>('recording-started', focusRecording, [focusRecording])
  useWebRecorderPubSubSubscribe<{ subjectId: string }>('focus-subject', focusRecording, [focusRecording])

  const recordingInProgress = recordingState?.mode === 'recording'

  return (
    renderableElementCount > 0 && (
      <div className="flex flex-col gap-2">
        {renderableElementCount > 1 && (
          <div className="flex items-center gap-x-2 self-center text-secondary select-none">
            <ChevronLeftIcon
              className={`h-4 ${currentIndex === 0 ? 'icon-tertiary' : 'cursor-pointer'}`}
              onClick={handlePrev}
            />
            <span>
              {currentIndex + 1} of {renderableElementCount}
            </span>
            <ChevronRightIcon
              className={`h-4 ${currentIndex === renderableElementCount - 1 ? 'icon-tertiary' : 'cursor-pointer'}`}
              onClick={handleNext}
            />
          </div>
        )}

        <div className="flex flex-col text-sm mx-7 p-3 border border-secondary rounded-md" id="live-notetaker">
          {callBotDispatch &&
            callBotLayoutInfos.map((layoutInfo, index) =>
              index === currentIndex ? (
                <CallBotSidebarView
                  key={layoutInfo.id}
                  layoutInfo={layoutInfo}
                  canJoinMyCall={canJoinMyCall}
                  canUseWebRecorder={canUseWebRecorder}
                  recordingInProgress={recordingInProgress}
                  dispatch={callBotDispatch}
                />
              ) : null
            )}
          {localRecordings.map((recording, index) =>
            index + callBotLayoutInfos.length === currentIndex ? (
              <UnsubmittedRecordingView
                key={'meetingId' in recording ? recording.meetingId : recording.intRefId}
                recording={recording}
                recordingState={recordingState}
                canDownload={canDownload}
                showDownloadButtonBeforeRecording={showDownloadButtonBeforeRecording}
                recordingDispatch={recordingDispatch}
                uploadRecording={upload}
                hideRecording={() => {
                  removeRecording(recording)
                  setCurrentIndex(Math.max(0, currentIndex - 1))
                }}
                yieldRecordingToMeetingDetail={() => {
                  if (defaultSubjectId && recordingDispatch) {
                    recordingDispatch({ action: 'set-subject-id', subjectId: defaultSubjectId, startRecording: 'no' })
                  }
                }}
                trackAnalytics={(msg: string) => trackAnalytics(hook, msg)}
              />
            ) : null
          )}
        </div>
      </div>
    )
  )
}
